/**
 * Alarm extractors.
 *
 * Splits into three concerns:
 *
 *   - `mapPlantAlarmRow(row)` projects one Cypher row from
 *     `PLANT_ALARMS_CYPHER` into a typed {@link PlantAlarm}, defaulting
 *     a missing priority to `'Medium'`.
 *   - `groupAlarmsByDefinition(alarms)` buckets a flat alarm list into
 *     UDTDefinition > UDTInstance groups so the tree view renders
 *     deterministically; standalone tag alarms land in `ungrouped`.
 *   - `normalizeActiveAlarm(raw, gatewayUrl)` + `canonicalizeLiveState`
 *     coerce one Ignition `system.alarm.queryStatus` row into a typed
 *     {@link PlantActiveAlarm}, dropping rows missing required fields.
 *
 * Pure functions, no RTK / no Ignition driver — trivially unit-testable.
 */

import { extractNode } from '../graph-row'
import { extractGateway } from '../refs'
import { compareByNameThenId } from '../sort'
import {
  PLANT_ALARM_PRIORITIES,
  type PlantActiveAlarm,
  type PlantAlarm,
  type PlantAlarmDefinitionGroup,
  type PlantAlarmLiveState,
  type PlantAlarmPriority,
  type PlantAlarmsIndex,
  type PlantTag
} from '../types'

/**
 * Project one Cypher row from `PLANT_ALARMS_CYPHER` into a typed
 * {@link PlantAlarm}. Drops rows where the alarm or source tag is
 * missing (sentinel `OPTIONAL MATCH` nulls). Coerces an unknown
 * priority to the spec's `'Medium'` default so the priority filter
 * stays well-behaved against legacy ingest.
 */
export function mapPlantAlarmRow(row: Record<string, unknown>): PlantAlarm | null {
  const alarmNode = extractNode<PlantAlarm>(row['alarm'])
  if (!alarmNode) return null
  const tag = extractNode<PlantTag>(row['tag'])
  if (!tag) return null
  const def = extractNode<{ id: string; _id: string; name: string }>(row['definition'])
  const inst = extractNode<{
    id: string
    _id: string
    name: string
    tag_path?: string
  }>(row['instance'])
  const gateway = extractGateway(row['gateway'])

  const priorityRaw = (alarmNode as unknown as { priority?: unknown }).priority
  const priority: PlantAlarmPriority =
    typeof priorityRaw === 'string' &&
    (PLANT_ALARM_PRIORITIES as readonly string[]).includes(priorityRaw)
      ? (priorityRaw as PlantAlarmPriority)
      : 'Medium'

  return {
    ...alarmNode,
    priority,
    tag_id: tag.id,
    tag_name: tag.name,
    tag_path: (alarmNode as unknown as { tag_path?: string }).tag_path ?? tag.tag_path,
    definition: def ? { id: def.id, name: def.name } : null,
    instance: inst ? { id: inst.id, name: inst.name, tag_path: inst.tag_path } : null,
    gateway: gateway && gateway.gateway_url ? gateway : null
  }
}

/**
 * Bucket a flat alarm list into UDTDefinition > UDTInstance groups so
 * the tree view renders deterministically. Standalone tag alarms (no
 * `definition`) land in `ungrouped`, mirroring the "Ungrouped" /
 * strays pattern used elsewhere in Plant View. Groups + per-group
 * instance lists are name-sorted for stable rendering across refreshes.
 */
export function groupAlarmsByDefinition(alarms: PlantAlarm[]): PlantAlarmsIndex {
  const groupsByDefId = new Map<string, PlantAlarmDefinitionGroup>()
  const ungrouped: PlantAlarm[] = []
  for (const alarm of alarms) {
    if (!alarm.definition) {
      ungrouped.push(alarm)
      continue
    }
    const defId = alarm.definition.id
    let group = groupsByDefId.get(defId)
    if (!group) {
      group = {
        definition: alarm.definition,
        instances: [],
        alarmCount: 0
      }
      groupsByDefId.set(defId, group)
    }
    const instKey = alarm.instance?.id ?? `__no_instance__:${alarm.tag_id}`
    let instGroup = group.instances.find((g) =>
      alarm.instance ? g.instance.id === instKey : g.instance.id === instKey
    )
    if (!instGroup) {
      instGroup = {
        instance: alarm.instance ?? {
          id: instKey,
          name: alarm.tag_name ?? '(no instance)',
          tag_path: alarm.tag_path
        },
        alarms: []
      }
      group.instances.push(instGroup)
    }
    instGroup.alarms.push(alarm)
    group.alarmCount += 1
  }

  const groups = Array.from(groupsByDefId.values()).sort((a, b) =>
    compareByNameThenId(a.definition, b.definition)
  )
  for (const g of groups) {
    g.instances.sort((a, b) => compareByNameThenId(a.instance, b.instance))
  }

  return { alarms, groups, ungrouped }
}

/**
 * Map any of Ignition's two state encodings into the canonical
 * `PlantAlarmLiveState`:
 *
 *   - **Enum name** (`ActiveUnacked` / `ActiveAcked` / `ClearUnacked`
 *     / `ClearAcked`) — what you'd get from `state.name()` in Jython.
 *   - **Human form** (`Active, Unacknowledged` / `Active, Acknowledged`
 *     / `Cleared, Unacknowledged` / `Cleared, Acknowledged`) — what
 *     `str(alarm.getState())` actually emits because Ignition's
 *     `AlarmState.toString()` is overridden to the display name. This
 *     is what the in-tree `getAlarms` webdev historically returned,
 *     so we must accept it for any gateway still running an older
 *     copy of the script.
 *
 * Comparison is case-insensitive and tolerant of whitespace around the
 * comma so a `"Active,Unacknowledged"` or `"active, unacked"` variant
 * still resolves. Unknown values yield `null` so the caller can drop
 * the row instead of silently misclassifying it.
 */
export function canonicalizeLiveState(raw: string): PlantAlarmLiveState | null {
  const k = raw.trim().toLowerCase().replace(/\s+/g, '')
  switch (k) {
    case 'activeunacked':
    case 'active,unacknowledged':
    case 'active,unacked':
      return 'ActiveUnacked'
    case 'activeacked':
    case 'active,acknowledged':
    case 'active,acked':
      return 'ActiveAcked'
    case 'clearunacked':
    case 'cleared,unacknowledged':
    case 'clear,unacknowledged':
    case 'cleared,unacked':
    case 'clear,unacked':
      return 'ClearUnacked'
    case 'clearacked':
    case 'cleared,acknowledged':
    case 'clear,acknowledged':
    case 'cleared,acked':
    case 'clear,acked':
      return 'ClearAcked'
    default:
      return null
  }
}

/**
 * Coerce one raw row from `/live-data/alarms/active` into a typed
 * `PlantActiveAlarm`. Stamps the originating `gateway_url` so the
 * caller can fan results back out to multi-gateway plants. Rows with
 * a missing `id`/`source` or unrecognized `state` are dropped — the
 * Plant View needs both to match back to a configured alarm.
 */
export function normalizeActiveAlarm(raw: unknown, gatewayUrl: string): PlantActiveAlarm | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const id = typeof r['id'] === 'string' ? (r['id'] as string) : null
  const source = typeof r['source'] === 'string' ? (r['source'] as string) : null
  const stateRaw = typeof r['state'] === 'string' ? (r['state'] as string) : null
  if (!id || !source || !stateRaw) return null
  const state = canonicalizeLiveState(stateRaw)
  if (!state) return null

  return {
    gateway_url: gatewayUrl,
    id,
    source,
    state,
    displayPath: typeof r['displayPath'] === 'string' ? (r['displayPath'] as string) : undefined,
    priority: typeof r['priority'] === 'string' ? (r['priority'] as string) : undefined,
    eventTime: typeof r['eventTime'] === 'number' ? (r['eventTime'] as number) : undefined,
    ackTime: typeof r['ackTime'] === 'number' ? (r['ackTime'] as number) : undefined,
    clearedTime: typeof r['clearedTime'] === 'number' ? (r['clearedTime'] as number) : undefined
  }
}
